<script setup async>
import {PlanApproval} from '@/common/api.service'
import {useRoute, useRouter} from 'vue-router'
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import shared from '@/common/shared'
import {useDropzone} from 'vue3-dropzone'
import {useToast} from 'vue-toastification'
import moment from 'moment/moment'
import ECP from '@/components/partial/ECP.vue'
import ValidateInput from '@/components/validate/input.vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import Pagination from '@/components/partial/Pagination.vue'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const list = ref({
  columns: [], directories: {}, meta: {
    total: 0, per_page: 0, total_filtered: 0, PLANS: [], USER_CONSONANTS: []
  }
})
const currentPage = ref(1)
const currentLimit = ref(10)
const plan = ref({
  UF_PLAN_ID: 0, USER_SIGNS: [0, 0, 0, 0, 0]
})
const docFiles = ref([])
const request = ref({
  is_initial_load: 1, page: currentPage.value, limit: currentLimit.value, filters: {}
})
const disableSave = ref(true)
const showStatus = ref(false)
const showSign = ref(false)
const dragStarted = ref(false)
const innovationDisable = ref(false)
const validationErrors = ref({})
const showEditPlanModal = ref(false)
const calendarInputClass = ref(VueDatePicker.props.inputClassName.default)
const dbl = ref(0)
const disableEdit = ref(false);

if (route.query._r) {
  request.value = JSON.parse(route.query._r)
  currentPage.value = request.value.page
  currentLimit.value = request.value.limit
}
const pages = computed(() => {
  if (
      !list.value.meta.total_filtered ||
      list.value.meta.total_filtered <= list.value.meta.per_page
  ) {
    return [];
  }
  return [
    ...Array(Math.ceil(list.value.meta.total_filtered / list.value.meta.per_page)).keys()
  ].map(e => e + 1);
})

watch(currentLimit, async () => {
  request.value.limit = currentLimit.value
  await fetchList()
})

watch(currentPage, async () => {
  request.value.page = currentPage.value
  await fetchList()
})

async function fetchList() {
  await PlanApproval.list(request.value)
      .then(({data}) => {
        list.value = data
        if (data.order) {
          request.value.order = data.order
        }
        request.value.filters = data.filters
        request.value.order_empty = 1
        delete request.value.is_initial_load
        router.push({
          query: {
            _r: JSON.stringify(request.value)
          }
        })
      }).catch(() => {
      })
}

await fetchList()

const columnList = computed(() => {
  return list.value.columns.filter((el) => el.visible)
})

function setValidationErrors(dt) {
  if (!dt) {
    validationErrors.value = {
      UF_AMOUNT: [],
      UF_EDITORIAL_NUMBER: [],
      UF_DATE_FORMATION_PP: [],
      DOCUMENTS: []
    }
  } else {
    validationErrors.value = dt
  }
}

async function routeNewPlan() {
  await router.push({name: 'ListApprovePurchaseAdd', query: {_r: route.query._r}})
}

async function showNewPlan() {
  docFiles.value = []
  plan.value = {
    UF_PLAN_ID: list.value.meta.PLANS[0].ID,
    UF_NAME_APPROVAL_PP_ID: 0,
    USER_SIGNS: [0, 0, 0, 0, 0]
  }
  list.value.meta.USER_CONSONANTS.filter(v => v.PROFILE.UF_IS_DEFAULT_AGREEMENT === true).forEach((v, index) => {
    if (index > 4) {
      return
    }
    plan.value.USER_SIGNS[index] = v.ID
  })
  setValidationErrors()
  innovationDisable.value = false
  shared.showModal('#modal-add-plan-zakupku-ypz')
  dbl.value++
}

async function saveNewPlan() {
  let formData = new FormData()
  formData.append('UF_PLAN_ID', plan.value.UF_PLAN_ID)
  formData.append('UF_NAME_APPROVAL_PP_ID', plan.value.UF_NAME_APPROVAL_PP_ID)
  formData.append('UF_EDITORIAL_NUMBER', plan.value.UF_EDITORIAL_NUMBER)
  formData.append('UF_DATE_FORMATION_PP', plan.value.UF_DATE_FORMATION_PP)
  formData.append('UF_AMOUNT', plan.value.UF_AMOUNT)
  formData.append('UF_COMMENT', plan.value.UF_COMMENT)
  plan.value.USER_SIGNS.forEach((v) => {
    if (v > 0) {
      formData.append('USER_SIGNS[]', v)
    }
  })
  docFiles.value.forEach((v, k) => {
    formData.append(`USER_DOCUMENTS[${k}]`, v)
  })
  await PlanApproval.create(formData)
      .then(async () => {
        await fetchList()
        shared.closeModal('#modal-add-plan-zakupku-ypz')
        await router.push({name: 'ListApprovePurchase', query: {_r: route.query._r}})
        dbl.value++
      }).catch(({response}) => {
        validationErrors.value = response.data.errors
        if (response.data.errors.UF_DATE_FORMATION_PP) {
          calendarInputClass.value = VueDatePicker.props.inputClassName.default + ' unvalidate'
        } else {
          calendarInputClass.value = VueDatePicker.props.inputClassName.default
        }
        toast.error(response.data.message, {
          timeout: 5000
        })
        dbl.value++
      })
}

let planUnwatch = function () {
}

async function routeEditPlan(item) {
  await router.push({name: 'ListApprovePurchaseEdit', params: {id: item}, query: {_r: route.query._r}})
}

async function showEditPlan(item) {
  disableSave.value = true
  disableEdit.value = false
  shared.closeModal('#plan-revoke')
  docFiles.value = []
  await PlanApproval.item(item)
      .then(async ({data}) => {
        data.UF_DATE_FORMATION_PP = moment(data.UF_DATE_FORMATION_PP).format('DD.MM.YYYY')
        plan.value = data
        let us = JSON.stringify(data.USER_SIGNS)
        plan.value.USER_SIGNS_STATUS = JSON.parse(us)
        data.USER_SIGNS.forEach((v, index) => {
          plan.value.USER_SIGNS[index] = v.UF_USER_ID
        })

        if (data.USER_SIGNS.length <= 4) {
          for (let i = data.USER_SIGNS.length; i <= 4; i++) {
            plan.value.USER_SIGNS[i] = 0;
          }
        }

        if (data.UF_COMMENT === 'undefined' || data.UF_COMMENT === undefined) {
          data.UF_COMMENT = '';
        }

        console.log(data.USER_SIGNS.length);

        if (plan.value.STATUS.UF_CODE === 'agreed' || !plan.value.IS_REVOKE) {
          disableEdit.value = true
        }

        planUnwatch = watch(
            plan,
            async () => {
              disableSave.value = false
            },
            {deep: true}
        )
        showEditPlanModal.value = true
        shared.showModal('#modal-plan-zakupku-ypz')
        dbl.value++
      }).catch(({response}) => {
        toast.error(response.data.message, {
          timeout: 5000
        })
        dbl.value++
      })
}

async function savePlan() {
  let formData = new FormData()
  formData.append('UF_EDITORIAL_NUMBER', plan.value.UF_EDITORIAL_NUMBER)
  formData.append('UF_DATE_FORMATION_PP', plan.value.UF_DATE_FORMATION_PP)
  formData.append('UF_AMOUNT', plan.value.UF_AMOUNT)
  formData.append('UF_COMMENT', plan.value.UF_COMMENT)
  plan.value.USER_SIGNS.forEach((v) => {
    if (v > 0) {
      formData.append('USER_SIGNS[]', v)
    }
  })

  let userDocumentIndex = 0
  await plan.value.USER_DOCUMENTS.forEach((v) => {
    formData.append(`USER_DOCUMENTS[${userDocumentIndex++}]`, v.ID)
  })
  docFiles.value.forEach((v) => {
    formData.append(`USER_DOCUMENTS[${userDocumentIndex++}]`, v)
  })
  await PlanApproval.update(plan.value.ID, formData)
      .then(async () => {
        await fetchList()
        await closeEditPlan()
        dbl.value++
      }).catch(({response}) => {
        validationErrors.value = response.data.errors
        if (response.data.errors.UF_DATE_FORMATION_PP) {
          calendarInputClass.value = VueDatePicker.props.inputClassName.default + ' unvalidate'
        } else {
          calendarInputClass.value = VueDatePicker.props.inputClassName.default
        }
        toast.error(response.data.message, {
          timeout: 5000
        })
        dbl.value++
      })
}

async function closeEditPlan() {
  planUnwatch()
  showEditPlanModal.value = false
  await router.push({name: 'ListApprovePurchase', query: {_r: route.query._r}})
  shared.closeModal('#modal-plan-zakupku-ypz')
}

const {getRootProps, getInputProps} = useDropzone({onDrop})

function onDrop(acceptFiles) {
  validationErrors.value.USER_DOCUMENTS = []
  acceptFiles.forEach(v => {
    docFiles.value.push(v)
  })
  disableSave.value = false
  dragStarted.value = false
}

const dragEnter = () => {
  dragStarted.value = true
}
const dragEnd = () => {
  console.log('dragEnd')
}

async function onAddDocFile() {

}

async function planAllDocs(id) {
  await PlanApproval.allDocs(id)
      .then(async ({data}) => {
        document.location.href = data.url
      }).catch(({response}) => {
        toast.error(response.data.message, {
          timeout: 5000
        })
      })
}

async function signList(id) {
  await PlanApproval.signList(id)
      .then(async ({data}) => {
        document.location.href = data.url
      }).catch(({response}) => {
        toast.error(response.data.message, {
          timeout: 5000
        })
      })
}

async function showSignStatus(item) {
  await PlanApproval.item(item)
      .then(async ({data}) => {
        data.UF_DATE_FORMATION_PP = moment(data.UF_DATE_FORMATION_PP).format('DD.MM.YYYY')
        plan.value = data
        showStatus.value = true
        shared.showModal('#modal-plan-zakupku-ypz-signatories')
      }).catch(({response}) => {
        toast.error(response.data.message, {
          timeout: 5000
        })
      })
}

async function showPlanRevoke() {
  shared.closeModal('#modal-plan-zakupku-ypz')
  showEditPlanModal.value = false
  shared.showModal('#plan-revoke')
}

async function planRevoke() {
  await PlanApproval.revoke(plan.value.ID)
      .then(async () => {
        await fetchList()
        shared.closeModal('#plan-revoke')
      }).catch(({response}) => {
        toast.error(response.data.message, {
          timeout: 5000
        })
      })
}

async function showSignModal() {
  shared.closeModal('#modal-plan-zakupku-ypz')
  showSign.value = true
}

async function closeModalSign() {
  showSign.value = false
  shared.showModal('#modal-plan-zakupku-ypz')
  await showEditPlan(plan.value.ID)
}

async function sendEds(val) {
  await PlanApproval.sign(plan.value.ID, {
    UF_EDS: val,
  })
      .then(async () => {
        await fetchList(route.params.id)
        await closeModalSign()
      }).catch(({response}) => {
        toast.error(response.data.message, {
          timeout: 5000
        })
      })
}

async function innovationCheck() {
  if (plan.value.UF_NAME_APPROVAL_PP_ID > 0
      && list.value.meta.DIRECTORY_NAME.find(v => v.ID === parseInt(plan.value.UF_NAME_APPROVAL_PP_ID)).UF_CODE === 'plan_purchase_innovative_products') {
    plan.value.UF_EDITORIAL_NUMBER = '0'
    plan.value.UF_AMOUNT = '0.00'
    innovationDisable.value = true
  } else {
    innovationDisable.value = false
  }
}

function selectPlaceholderColor() {
  let selects = document.querySelectorAll('select.c-select')
  selects.forEach(select => {

    // Добавляем слушатель события изменения значения
    select.addEventListener('change', function () {

      if (this.value == 0) {
        this.style.color = ''
      } else {
        this.style.color = '#000'
      }
    })
  })
}

async function copyURL() {
  try {
    await navigator.clipboard.writeText(window.location.href)
    toast.success('Ссылка на карточку скопирована', {
      timeout: 2000
    })
  } catch ($e) {
    toast.error('При копировании ссылки на карточку произошла ошибка', {
      timeout: 2000
    })
  }
}

onMounted(async () => {
  await router.isReady()
  selectPlaceholderColor()
  if (route.name === 'ListApprovePurchaseAdd') {
    await showNewPlan()
  } else if (route.name === 'ListApprovePurchaseEdit') {
    await showEditPlan(route.params.id)
  }
})
onUnmounted(() => {
  selectPlaceholderColor()
})
</script>

<template>
  <div class="main-wraper" id="js-main-wraper">
    <MainHeader></MainHeader>
    <!-- header -->
    <section class="content-wrapper" id="js-content-wrapper">
      <div class="container-fluid">
        <section class="page-title-container stl-mb-30 stl-pt-20">
          <div class="page-title-container__col">
            <h1 class="title-page">Утверждение плана закупок</h1>
          </div>
          <div class="page-title-container__col page-title-container__controll">
            <a @click="routeNewPlan" v-if="list.meta.IS_EDIT"
               data-m class="btn btn-blue">Добавить план закупки</a>
          </div>
        </section>
        <!-- page-title-container -->

        <section class="page-container">
          <div class="box-stl c-table--container">
            <div class="holder-responsive">
              <table class="c-table c-table-interface-bs-b c-table-interface-mb-0">
                <thead class="thead-stl-fw-bold">
                <tr>
                  <th :key="column.name" v-for="column in columnList">
                    <div class="sorting-holder" v-if="column.type==='column'">
                      <span>{{ column.title }}</span>
                    </div>
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr :key="item.ID" v-for="item in list.data">
                  <td :key="column.name" v-for="column in columnList">
                                        <span
                                            v-if="column.name!=='STATUS' && (column.display_as === 'text' || column.display_as === 'text_path')"
                                            :style="{
                      'white-space': column.modifiers.find(val => val === 'white-space-pre')?'pre':'',
                      'white-space': column.name==='AWAITING_APPROVAL' ||  column.name==='AGREED'?'pre-line':'',
                      'word-break': column.modifiers.find(val => val === 'break-all')?'break-all':'',
                      'color': column.display_as_config.text_color_path?item.STATUS.color:''
                    }"
                                        >{{
                                            shared.showField(column, item)
                                          }}
                                        </span>
                    <a class="stl-clr-blue stl-clr-td-n" href="#" v-if="column.name==='PLAN'"
                       @click="routeEditPlan(item.ID)">
                      {{ shared.showField(column, item).UF_YEAR }}
                    </a>
                    <a
                        :class="(column.display_as_config.text_color_path && item.STATUS.color)?'stl-clr-'+item.STATUS.color:'stl-clr-orange'"
                        class=" stl-clr-td-n"
                        href="#"
                        :style="{
                      'white-space': column.modifiers.find(val => val === 'white-space-pre')?'pre':'',
                      'word-break': column.modifiers.find(val => val === 'break-all')?'break-all':'',
                      'color_': column.display_as_config.text_color_path?item.STATUS.color:''
                    }"
                        v-if="column.name==='STATUS'" @click="showSignStatus(item.ID)">
                      {{ shared.showField(column, item) }}
                    </a>
                  </td>
                </tr>
                </tbody>
              </table>
              <Pagination
                  :pages="pages"
                  v-model="currentPage"
                  :total="list.meta.total_filtered"
                  :perPage="currentLimit"
                  @update:perPage="currentLimit = $event"
              ></Pagination>
            </div>
          </div>
        </section>
        <!-- page-container -->
      </div>
    </section>
    <MainFooter>
      <div class="footer-right__controll">

      </div>
    </MainFooter>
    <!-- footer -->
  </div>
  <!-- main-wraper -->
  <!-- Утверждение Плана закупки - Добавить План закупки  -->
  <div class="modal" id="modal-add-plan-zakupku-ypz" tabindex="-1" @dragenter="dragEnter" @dragleave="dragEnd">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Добавить План закупки </h5>
        </div>
        <div class="modal-body">
          <form action="#">
            <div class="modal-grid-row modal-grid-row-stl --jsb">
              <div class="df-fc">
                <label for="" class="form-label">План<span
                    class="c-c-danger">*</span></label>
                <select v-model="plan.UF_PLAN_ID" class="c-input c-select c-select--black">
                  <option :value="i.ID" v-for="i in list.meta.PLANS" :key="i">{{ i.UF_YEAR }}</option>
                </select>
              </div>
              <div class="df-fc">
                <ValidateInput
                    :name="'Наименование'"
                    :unselect="'Выберите из списка'"
                    :unselect-value="0"
                    required
                    select
                    :items="list.meta.DIRECTORY_NAME"
                    :item-key="'ID'"
                    :item-name="'UF_NAME'"
                    :errors="validationErrors.UF_NAME_APPROVAL_PP_ID"
                    label-class="form-label"
                    @change="innovationCheck"
                    v-model="plan.UF_NAME_APPROVAL_PP_ID">
                </ValidateInput>
              </div>
              <div class="df-fc">
                <ValidateInput :placeholder="'№ версии'"
                               :name="'Редакция'"
                               :show-errors="false"
                               number
                               rounded
                               :disabled="innovationDisable"
                               :required="true"
                               :errors="validationErrors.UF_EDITORIAL_NUMBER"
                               label-class="form-label"
                               v-model="plan.UF_EDITORIAL_NUMBER"></ValidateInput>
              </div>
              <div class="df-fc c-filter__item-data-inp">
                <ValidateInput
                    :name="'Дата формирования ПЗ'"
                    :required="true"
                    :errors="validationErrors.UF_DATE_FORMATION_PP"
                    label-class="form-label"
                    :show-errors="false"
                    v-model="plan.UF_DATE_FORMATION_PP">
                  <template v-slot:field="{}">
                    <VueDatePicker
                        :input-class-name="calendarInputClass"
                        placeholder="00.00.0000"
                        v-model="plan.UF_DATE_FORMATION_PP" format="dd.MM.yyyy"
                        model-type="format"/>
                  </template>
                </ValidateInput>
              </div>
              <div class="df-fc c-input--black">
                <ValidateInput :placeholder="'Введите сумму'"
                               :name="'Сумма'"
                               :show-errors="false"
                               number
                               :required="true"
                               :spaces="true"
                               :errors="validationErrors.UF_AMOUNT"
                               :disabled="innovationDisable"
                               :float-format="0"
                               label-class="form-label"
                               v-model="plan.UF_AMOUNT"></ValidateInput>
              </div>
            </div>
            <div class="modal-grid-row modal-grid-row-stl">
              <div class="df-fc">
                <label for="" class="form-label">Комментарий</label>
                <textarea class="c-input c-textarea c-input--black c-textarea-n-resize" placeholder="Ваш текст"
                          v-model="plan.UF_COMMENT"></textarea>
              </div>
            </div>
            <div class="modal-grid-row modal-grid-row-stl --fw">
              <div class="df-fc">
                <label for="" class="form-label">Согласующий №1
                  <span class="c-c-danger">*</span>
                </label>
                <select v-model="plan.USER_SIGNS[0]" id="" class="c-input c-select"
                        :style="(plan.USER_SIGNS[0] > 0) ? 'color:#000' : ''">
                  <option :value="0">Выберите из списка</option>
                  <option :value="i.ID" v-for="i in list.meta.USER_CONSONANTS" :key="i">{{ i.FULL_NAME }}</option>
                </select>
              </div>
              <div class="df-fc">
                <label for="" class="form-label">Согласующий №2</label>
                <select v-model="plan.USER_SIGNS[1]" id="" class="c-input c-select"
                        :style="(plan.USER_SIGNS[1] > 0) ? 'color:#000' : ''">
                  <option :value="0">Выберите из списка</option>
                  <option :value="i.ID" v-for="i in list.meta.USER_CONSONANTS" :key="i">{{ i.FULL_NAME }}</option>
                </select>
              </div>
              <div class="df-fc">
                <label for="" class="form-label">Согласующий №3</label>
                <select v-model="plan.USER_SIGNS[2]" id="" class="c-input c-select"
                        :style="(plan.USER_SIGNS[2] > 0) ? 'color:#000' : ''">
                  <option :value="0">Выберите из списка</option>
                  <option :value="i.ID" v-for="i in list.meta.USER_CONSONANTS" :key="i">{{ i.FULL_NAME }}</option>
                </select>
              </div>
              <div class="df-fc">
                <label for="" class="form-label">Согласующий №4</label>
                <select v-model="plan.USER_SIGNS[3]" id="" class="c-input c-select"
                        :style="(plan.USER_SIGNS[3] > 0) ? 'color:#000' : ''">
                  <option :value="0">Выберите из списка</option>
                  <option :value="i.ID" v-for="i in list.meta.USER_CONSONANTS" :key="i">{{ i.FULL_NAME }}</option>
                </select>
              </div>
              <div class="df-fc">
                <label for="" class="form-label">Согласующий №5</label>
                <select v-model="plan.USER_SIGNS[4]" id="" class="c-input c-select"
                        :style="(plan.USER_SIGNS[4] > 0) ? 'color:#000' : ''">
                  <option :value="0">Выберите из списка</option>
                  <option :value="i.ID" v-for="i in list.meta.USER_CONSONANTS" :key="i">{{ i.FULL_NAME }}</option>
                </select>
              </div>
            </div>
            <div class="modal-grid-row" :class="{
                      unvalidate: validationErrors.DOCUMENTS && validationErrors.DOCUMENTS.length > 0
                    }">
              <div class="h4-title stl-mb-10">Документы <span class="c-c-danger">*</span></div>
              <ol class="documents-list">
                <li v-for="(docFile, index) in docFiles" :key="docFile">
                  <div class="df-ac">
                    <span class="stl-mr-10">{{ docFile.name }}</span>
                    <span class="stl-cursor-pointer df-ac holder-delete-file" @click="docFiles.splice(index, 1)"></span>
                  </div>
                </li>
              </ol>
              <div class="file-upload-multiple" v-bind="getRootProps()" :class="{
                'drag-started':dragStarted,
                'unvalidate': validationErrors.USER_DOCUMENTS && validationErrors.USER_DOCUMENTS.length > 0
              }">
                <input v-bind="getInputProps()" :multiple="true" @change="onAddDocFile">
                <span class="file-upload-multiple-label">Выберите файлы или перетащите сюда</span>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-reset" @click="router.push({name: 'ListApprovePurchase', query: {_r: route.query._r}});
          shared.closeModal('#modal-add-plan-zakupku-ypz')">
            отменить
          </button>
          <button type="button" class="btn btn-blue" @click.once="saveNewPlan" :key="dbl">отправить на согласование
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- Утверждение Плана закупки - Добавить План закупки  -->

  <!-- Утверждение Плана закупки - Утверждение Плана закупки -->
  <div class="modal" id="modal-plan-zakupku-ypz" tabindex="-1">
    <div class="modal-dialog modal-xl modal-dialog-centered" v-if="plan.USER_SIGNS_STATUS">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Утверждение Плана закупки
            <span class="stl-clr-red" v-if="plan.STATUS.UF_CODE === 'revoked'">(отозвано)</span>
          </h5>
          <a
              href="javascript:void(0);"
              @click="copyURL"
              style="text-decoration: underline; float: right; padding-right: 10px;"
          >Ссылка</a>
        </div>
        <div class="modal-body">
          <form action="#" v-if="showEditPlanModal">
            <div class="modal-grid-row modal-grid-row-stl --jsb">
              <div class="df-fc">
                <label for="" class="form-label">План<span
                    class="c-c-danger">*</span></label>
                <select v-model="plan.UF_PLAN_ID"
                        :disabled="true"
                        class="c-input c-select c-select--black">
                  <option :value="i.ID" v-for="i in list.meta.PLANS" :key="i">{{ i.UF_YEAR }}</option>
                </select>
              </div>
              <div class="df-fc c-input--black input">
                <ValidateInput
                    :name="'Наименование'"
                    :unselect="'Выберите из списка'"
                    :unselect-value="0"
                    required
                    select
                    :items="list.meta.DIRECTORY_NAME"
                    :item-key="'ID'"
                    :item-name="'UF_NAME'"
                    :errors="validationErrors.UF_NAME_APPROVAL_PP_ID"
                    label-class="form-label"
                    @change="innovationCheck"
                    :disabled="true"
                    v-model="plan.UF_NAME_APPROVAL_PP_ID">
                </ValidateInput>
              </div>
              <div class="df-fc">
                <ValidateInput :placeholder="'№ версии'"
                               :name="'Редакция'"
                               :show-errors="false"
                               number
                               rounded
                               :disabled="innovationDisable || disableEdit"
                               :required="true"
                               :errors="validationErrors.UF_EDITORIAL_NUMBER"
                               label-class="form-label"
                               v-model="plan.UF_EDITORIAL_NUMBER"></ValidateInput>
              </div>
              <div class="df-fc c-filter__item-data-inp">
                <ValidateInput
                    :name="'Дата формирования ПЗ'"
                    :required="true"
                    :errors="validationErrors.UF_DATE_FORMATION_PP"
                    label-class="form-label"
                    :disabled="disableEdit"
                    :show-errors="false"
                    v-model="plan.UF_DATE_FORMATION_PP">
                  <template v-slot:field="{}">
                    <VueDatePicker
                        :input-class-name="calendarInputClass"
                        placeholder="00.00.0000"
                        :disabled="disableEdit"
                        v-model="plan.UF_DATE_FORMATION_PP" format="dd.MM.yyyy"
                        model-type="format"/>
                  </template>
                </ValidateInput>
              </div>
              <div class="df-fc c-input--black">
                <ValidateInput :placeholder="'Введите сумму'"
                               :name="'Сумма'"
                               :show-errors="false"
                               number
                               :required="true"
                               :spaces="true"
                               :errors="validationErrors.UF_AMOUNT"
                               :disabled="innovationDisable || disableEdit"
                               :float-format="0"
                               label-class="form-label"
                               v-model="plan.UF_AMOUNT"></ValidateInput>
              </div>
            </div>
            <div class="modal-grid-row modal-grid-row-stl">
              <div class="df-fc">
                <label for="" class="form-label">Комментарий</label>
                <textarea class="c-input c-textarea c-input--black c-textarea-n-resize" placeholder="Ваш текст"
                          v-model="plan.UF_COMMENT"
                          :disabled="plan.STATUS.UF_CODE === 'revoked' || disableEdit"></textarea>
              </div>
            </div>
            <div class="modal-grid-row modal-grid-row-stl --fw">
              <div class="df-fc" v-for="i in [0,1,2,3,4]" :key="i">
                <label for="" class="form-label">Согласующий №{{ i + 1 }} </label>
                <select v-model="plan.USER_SIGNS[i]"
                        id=""
                        class="c-input c-select--black_ c-select"
                        :disabled="disableEdit || plan.STATUS.UF_CODE === 'revoked' || (plan.USER_SIGNS_STATUS[i] && plan.USER_SIGNS_STATUS[i].STATUS
                && plan.USER_SIGNS_STATUS[i].STATUS.UF_CODE !== 'on_approval')"
                        :style="(plan.USER_SIGNS[i] > 0) ? 'color:#000' : ''">
                  <option :value="0">Выберите из списка</option>
                  <option :value="i.ID" v-for="i in list.meta.USER_CONSONANTS" :key="i">{{ i.FULL_NAME }}</option>
                </select>
              </div>
            </div>
            <div class="modal-grid-row">
              <div class="h4-title stl-mb-10 df-ac">
                Документы
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
                     class="stl-cursor-pointer"
                     @click="planAllDocs(plan.ID)"
                     v-if="plan.USER_DOCUMENTS.length >0">
                  <mask id="mask0_550_4065" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="20"
                        height="20">
                    <rect width="20" height="20" fill="#D9D9D9"/>
                  </mask>
                  <g mask="url(#mask0_550_4065)">
                    <path
                        d="M14.3751 14.8381L13.2645 13.7275C13.149 13.6122 13.0054 13.5546 12.8334 13.5546C12.6615 13.5546 12.5178 13.6122 12.4024 13.7275C12.287 13.8429 12.2292 13.9884 12.2292 14.164C12.2292 14.3395 12.287 14.4841 12.4024 14.5977L14.4728 16.6763C14.6235 16.8269 14.7992 16.9023 15.0001 16.9023C15.2009 16.9023 15.3767 16.8269 15.5274 16.6763L17.6059 14.5977C17.7212 14.4841 17.7788 14.3395 17.7788 14.164C17.7788 13.9884 17.7212 13.8429 17.6059 13.7275C17.4905 13.6122 17.345 13.5546 17.1695 13.5546C16.9939 13.5546 16.8493 13.6122 16.7357 13.7275L15.6251 14.8381V11.6875C15.6251 11.5104 15.5652 11.362 15.4453 11.2423C15.3254 11.1224 15.177 11.0625 14.9999 11.0625C14.8227 11.0625 14.6742 11.1224 14.5547 11.2423C14.4349 11.362 14.3751 11.5104 14.3751 11.6875V14.8381ZM12.5001 18.5417H17.5001C17.6772 18.5417 17.8256 18.6016 17.9453 18.7215C18.0652 18.8413 18.1251 18.9898 18.1251 19.1669C18.1251 19.3441 18.0652 19.4925 17.9453 19.6121C17.8256 19.7318 17.6772 19.7917 17.5001 19.7917H12.5001C12.323 19.7917 12.1746 19.7317 12.0549 19.6119C11.935 19.492 11.8751 19.3435 11.8751 19.1665C11.8751 18.9892 11.935 18.8408 12.0549 18.7212C12.1746 18.6015 12.323 18.5417 12.5001 18.5417ZM5.04821 16.4583C4.63793 16.4583 4.28432 16.3098 3.98737 16.0127C3.69029 15.7158 3.54175 15.3622 3.54175 14.9519V3.38146C3.54175 2.97118 3.69029 2.61757 3.98737 2.32063C4.28432 2.02354 4.63793 1.875 5.04821 1.875H10.2101C10.4133 1.875 10.607 1.91403 10.7911 1.99208C10.9753 2.07 11.1358 2.17736 11.2726 2.31417L15.1859 6.2275C15.3227 6.36431 15.4301 6.52479 15.508 6.70896C15.5861 6.89313 15.6251 7.08681 15.6251 7.29V8.61063C15.6251 8.78771 15.5652 8.93611 15.4453 9.05583C15.3254 9.17569 15.177 9.23563 14.9999 9.23563C14.8227 9.23563 14.6742 9.17569 14.5547 9.05583C14.4349 8.93611 14.3751 8.78771 14.3751 8.61063V7.29167H11.3382C11.0245 7.29167 10.7577 7.18181 10.538 6.96208C10.3183 6.74236 10.2084 6.47563 10.2084 6.16188V3.125H5.04821C4.98404 3.125 4.92529 3.15174 4.87196 3.20521C4.81848 3.25854 4.79175 3.31729 4.79175 3.38146V14.9519C4.79175 15.016 4.81848 15.0748 4.87196 15.1281C4.92529 15.1816 4.98404 15.2083 5.04821 15.2083H9.42321C9.60029 15.2083 9.74869 15.2683 9.86841 15.3881C9.98828 15.508 10.0482 15.6565 10.0482 15.8335C10.0482 16.0108 9.98828 16.1592 9.86841 16.2787C9.74869 16.3985 9.60029 16.4583 9.42321 16.4583H5.04821Z"
                        fill="#0D74BB"/>
                  </g>
                </svg>
              </div>
              <ol class="documents-list">
                <li v-for="(docFile, index) in plan.USER_DOCUMENTS" :key="docFile">
                  <div class="df-ac">
                    <a class="stl-mr-10" :href="docFile.FILE.URL_DOWNLOAD">{{ docFile.FILE.FILE_NAME }}</a>
                    <span class="stl-cursor-pointer df-ac holder-delete-file"
                          v-if="plan.STATUS.UF_CODE !== 'revoked' && !disableEdit"
                          @click="plan.USER_DOCUMENTS.splice(index, 1);disableSave=false"></span>
                  </div>
                </li>
                <li v-for="(docFile, index) in docFiles" :key="docFile">
                  <div class="df-ac">
                    <span class="stl-mr-10">{{ docFile.name }}</span>
                    <span class="stl-cursor-pointer df-ac holder-delete-file"
                          @click="docFiles.splice(index, 1);disableSave=false"></span>
                  </div>
                </li>
              </ol>
            </div>
            <div class="modal-grid-row" v-if="plan.STATUS.UF_CODE !== 'revoked' && !disableEdit">
              <div class="file-upload-multiple" v-bind="getRootProps()" :class="{
                'drag-started':dragStarted,
                'unvalidate': validationErrors.USER_DOCUMENTS && validationErrors.USER_DOCUMENTS.length > 0
              }">
                <input v-bind="getInputProps()" :multiple="true" @change="onAddDocFile">
                <span class="file-upload-multiple-label"
                >Выберите файлы или перетащите сюда</span>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-green" v-if="plan.IS_SIGN && plan.STATUS.UF_CODE !== 'revoked'"
                  @click="showSignModal">подписать
          </button>
          <button type="button" class="btn btn-red" v-if="plan.IS_REVOKE" @click="showPlanRevoke">отозвать</button>
          <button type="button" class="btn btn-blue" v-if="plan.STATUS.UF_CODE !== 'revoked' && !disableEdit"
                  @click="savePlan" :key="dbl"
                  :class="{'--disabled': disableSave}"
                  :disabled="disableSave">
            сохранить изменения
          </button>
          <button type="button" class="btn "
                  :class="{
                    'btn-transparent':plan.STATUS.UF_CODE !== 'agreed',
                    'btn-blue': plan.STATUS.UF_CODE === 'agreed'
                  }"
                  :disabled="plan.STATUS.UF_CODE !== 'agreed'" @click="signList(plan.ID)">лист согласования
          </button>
          <button type="button" class="btn btn-reset" data-bs-dismiss="modal" @click="closeEditPlan">закрыть</button>
        </div>
      </div>
    </div>
  </div>
  <!-- Утверждение Плана закупки - Добавить План закупки  -->

  <!-- Утверждение Плана закупки - Лист согласование плана закупки-->
  <div class="modal" id="modal-plan-zakupku-ypz-signatories" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered" v-if="showStatus">
      <div class="modal-content">
        <div class="modal-header df-fc df-al stl-mb-15">
          <div style="border-bottom: 1px solid #cecece; padding-bottom: 8px;">
            <h5 class="modal-title stl-mb-5">Согласование плана закупки {{ plan.PLAN.UF_YEAR }} г., редакция №
              {{ plan.UF_EDITORIAL_NUMBER }}</h5>

            <template v-if="plan.STATUS.UF_CODE === 'revoked'">
              <span class="stl-fs-12 stl-clr-red">Отозвано - {{
                  moment(plan.UF_DATE_MODIFIED).format('DD.MM.YYYY')
                }}</span>
            </template>
            <template v-else>
              <span class="stl-fs-12">Дата создания - {{ moment(plan.UF_DATE_CREATE).format('DD.MM.YYYY') }}</span>
            </template>
          </div>
        </div>

        <div class="modal-body stl-mb-20">
          <form action="#">
            <div class="modal-grid-row">
              <ol class="list-signatories">
                <li v-for="(item, index) in plan.USER_SIGNS.filter(v => v.UF_USER_ID !== 0)" :key="item">
                  <span class="list-signatories__name">{{ item.USER.FULL_NAME }}</span>

                  <template v-if="plan.STATUS.UF_CODE !== 'revoked' && item.STATUS && item.STATUS.UF_NAME">
                    <span class="list-signatories__status stl-clr-green"
                          v-if="item.STATUS && item.STATUS.UF_CODE==='agreed'">
                      <template v-if="index === plan.USER_SIGNS.filter(v => v.UF_USER_ID !== 0).length - 1">
                          {{ item.STATUS.UF_NAME_LAST }}
                      </template>
                      <template v-else>
                          {{ item.STATUS.UF_NAME }}
                      </template>&nbsp;
                      <template v-if="item.UF_DATE_SIGN">
                        {{ moment(item.UF_DATE_SIGN).format('DD.MM.YYYY') }}
                      </template>
                    </span>
                    <span class="list-signatories__status stl-clr-orange" v-else>{{ item.STATUS.UF_NAME }}</span>
                  </template>
                </li>
              </ol>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-reset"
                  @click="showStatus = false;shared.closeModal('#modal-plan-zakupku-ypz-signatories')">
            закрыть
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal" id="plan-revoke" tabindex="-1">
    <div class="modal-dialog  modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Подтверждение</h5>
          <button type="button" class="btn-close" @click="shared.closeModal('#plan-revoke')"
                  aria-label="Закрыть"></button>
        </div>
        <div class="modal-body">
          <form action="#">
            <div class="modal-text">
              <p>Вы действительно хотите отозвать утверждение плана закупки?</p>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-reset" @click="showEditPlan(plan.ID)">Нет
          </button>
          <button type="button" class="btn btn-red" @click="planRevoke()">Да</button>
        </div>
      </div>
    </div>
  </div>
  <ECP :sign-title="'Подписать заявку на закупку'"
       :sign-data="plan.UF_HASH" :close-modal="closeModalSign"
       :sign-callback="sendEds" v-if="showSign"></ECP>
</template>
